import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

// Back / forward for the topbar arrows.
//
// The browser will not tell a page whether there is anywhere to go, so the
// two answers come from different places:
//
// BACK: React Router stamps each history entry it creates with its position
// (`history.state.idx`). At 0 this is the first screen of the app in this
// tab, and going back would leave the app — to the login page, Google,
// wherever the user came from. So ← is off there.
//
// FORWARD: there is no API at all. We remember the furthest position seen
// in this tab. A PUSH (opening a new screen) throws away everything ahead
// of it, exactly as the browser does, so the furthest position becomes the
// current one. Kept at module level so it survives the topbar remounting.
// After a full page reload we start from wherever we are: → stays off until
// the user goes back once, which is wrong only in the safe direction.

let furthest = -1;

const currentIdx = (): number => {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof idx === 'number' ? idx : 0;
};

export function useHistoryNav() {
  const location = useLocation();
  const navType = useNavigationType();
  const navigate = useNavigate();
  const [state, setState] = useState({ canBack: false, canForward: false });

  useEffect(() => {
    const idx = currentIdx();
    if (navType === 'PUSH' || furthest < 0) furthest = idx;
    else furthest = Math.max(furthest, idx);
    setState({ canBack: idx > 0, canForward: idx < furthest });
  }, [location.key, navType]);

  return {
    ...state,
    back: () => navigate(-1),
    forward: () => navigate(1),
  };
}

/** Test-only: forget the furthest position between tests. */
export const resetHistoryNav = () => { furthest = -1; };
